/**
 * PrintSizeRulesSection — per-CLIENT-org print dimensions (cm) by product
 * type and size (`org_print_size_rules`).
 *
 * A 3PL OWNER sets a client's rules on the client's behalf; the client
 * selector defaults to "This organization" and lists every active junction
 * client (`useOwnerClientTarget`, deduped by client org: a client
 * may appear once per location junction). Selecting a client re-targets both
 * the read (`printSizeRules.getRules`) and the write
 * (`printSizeRules.updateRules`) at that org via the `organizationId` input.
 *
 * At print/resize time an explicit rule OVERRIDES the artwork's own
 * dimensions (including a client on `resize_mode: 'source'`) — see
 * `resolvePrintSizeRule` / `cmToPx` (`@threadheads/shared`).
 *
 * Local-edit model: hydrate a local map from the query, edit locally, Save
 * persists via `updateRules`. Mirrors `RipRoutingSection`'s dirty-buffer +
 * admin-gated Save shape.
 *
 * CLIENT CODE TABLES (2026-08-04): the product-type and size code inputs are
 * `PrintSizeCodeCombobox` pick-lists sourced from the SELECTED CLIENT's own
 * `productSync.getCodeTablesForOrg` (never re-typed by the 3PL owner) — see
 * `architectural-decisions.md` § "Shared 3PL↔client data is surfaced, never
 * re-typed". The combobox degrades to today's plain free-text Input+Add
 * whenever that query hasn't loaded or failed, so the section itself is
 * never gated on it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Info, Loader2, Plus, Ruler, Save, Trash2 } from 'lucide-react';
import {
  PRINT_SIZE_CM_MAX,
  PRINT_SIZE_CM_MIN,
  PRINT_SIZE_NO_SIZE_KEY,
  RIP_CODE_REGEX,
  cmToPx,
  type PrintSizeRule,
  type PrintSizeRulesMap,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { useOwnerClientTarget } from '../hooks/useOwnerClientTarget';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PrintSizeCodeCombobox,
  type PrintSizeCodeComboboxStatus,
  type PrintSizeCodeOption,
} from './PrintSizeCodeCombobox';
import { joinStoreNames, buildProductTypeCodeOptions, codeTablesStatus } from './org-code-labels';

// ---------------------------------------------------------------------------
// Pure helpers (module-level so identity is stable).
// ---------------------------------------------------------------------------

const isCodeValid = (v: string): boolean => RIP_CODE_REGEX.test(v);
const isCmValid = (v: number): boolean =>
  Number.isFinite(v) && v >= PRINT_SIZE_CM_MIN && v <= PRINT_SIZE_CM_MAX;

/** Controlled-input formatting: an unset (0/NaN) rule dimension shows blank. */
const cmToInputValue = (v: number): string => (v > 0 ? String(v) : '');
const inputValueToCm = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** NO_SIZE sorts first (it's the type's default), the rest alphabetically. */
const sortSizeEntries = (a: [string, PrintSizeRule], b: [string, PrintSizeRule]): number => {
  if (a[0] === PRINT_SIZE_NO_SIZE_KEY) return -1;
  if (b[0] === PRINT_SIZE_NO_SIZE_KEY) return 1;
  return a[0].localeCompare(b[0]);
};

/** Drop invalid/empty leaves and empty type-entries before Save — Save is
 *  gated on `anyInvalid` too, so this is belt-and-braces. */
function cleanMapForSave(map: PrintSizeRulesMap): PrintSizeRulesMap {
  const out: PrintSizeRulesMap = {};
  for (const [pt, sizes] of Object.entries(map)) {
    const clean: Record<string, PrintSizeRule> = {};
    for (const [code, rule] of Object.entries(sizes)) {
      if (isCmValid(rule.widthCm) && isCmValid(rule.heightCm)) {
        clean[code] = { widthCm: rule.widthCm, heightCm: rule.heightCm };
      }
    }
    if (Object.keys(clean).length > 0) out[pt] = clean;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Section.
// ---------------------------------------------------------------------------

export function PrintSizeRulesSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  // "Applies to": own org first, then active junction clients (deduped,
  // sorted). `targetOrgInput` is undefined for the own org, so the router's
  // ctx.organizationId default targets the caller's own row that way too.
  const { ownOrgId, clientOptions, selectedOrgId, setSelectedOrgId, targetOrgInput } =
    useOwnerClientTarget();

  const rulesQuery = trpc.printSizeRules.getRules.useQuery(
    { organizationId: targetOrgInput },
    { enabled: !!selectedOrgId },
  );
  // The SELECTED CLIENT's own product-sync code tables, surfaced as
  // pick-lists below — same targetOrgInput convention as rulesQuery; the
  // section itself never gates on this query (PrintSizeCodeCombobox
  // degrades to plain free text while loading/errored).
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery(
    { organizationId: targetOrgInput },
    { enabled: true },
  );
  const utils = trpc.useUtils();

  const [localMap, setLocalMap] = useState<PrintSizeRulesMap>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>([]);

  // S3 review fix: reset the dirty buffer IMMEDIATELY on a client switch —
  // do NOT wait for the new org's query to resolve. Without this, a Save
  // clicked in the gap between switching clients and the new org's data
  // landing would write the PREVIOUS client's edited buffer to the NEWLY
  // selected client's row.
  useEffect(() => {
    setLocalMap({});
    setHasChanges(false);
  }, [selectedOrgId]);

  // Hydrate local state from the server (initial load, client switch, or
  // after a save invalidation).
  useEffect(() => {
    if (rulesQuery.data) {
      setLocalMap(rulesQuery.data.map ?? {});
      setHasChanges(false);
    }
  }, [rulesQuery.data]);

  const updateMutation = trpc.printSizeRules.updateRules.useMutation({
    onSuccess: () => {
      toast.success('Print size rules saved');
      setHasChanges(false);
      utils.printSizeRules.getRules.invalidate();
    },
    onError: (e) => toast.error(`Could not save: ${e.message}`),
  });

  const productTypeCodes = useMemo(
    () => Object.keys(localMap).sort((a, b) => a.localeCompare(b)),
    [localMap],
  );

  // Any leaf that would fail the write-path zod — blocks Save.
  const anyInvalid = useMemo(() => {
    for (const sizes of Object.values(localMap)) {
      for (const rule of Object.values(sizes)) {
        if (!isCmValid(rule.widthCm) || !isCmValid(rule.heightCm)) return true;
      }
    }
    return false;
  }, [localMap]);

  // ---- local-edit mutators ----

  const setRule = (pt: string, code: string, patch: Partial<PrintSizeRule>): void => {
    setLocalMap((prev) => {
      const sizes = prev[pt] ?? {};
      const existing = sizes[code] ?? { widthCm: 0, heightCm: 0 };
      return { ...prev, [pt]: { ...sizes, [code]: { ...existing, ...patch } } };
    });
    setHasChanges(true);
  };

  const removeSize = (pt: string, code: string): void => {
    setLocalMap((prev) => {
      const sizes = prev[pt];
      if (!sizes) return prev;
      const next = { ...sizes };
      delete next[code];
      return { ...prev, [pt]: next };
    });
    setHasChanges(true);
  };

  const removeProductType = (pt: string): void => {
    setLocalMap((prev) => {
      const next = { ...prev };
      delete next[pt];
      return next;
    });
    setHasChanges(true);
  };

  const addSize = (pt: string, rawCode: string): boolean => {
    const trimmed = rawCode.trim();
    const code = trimmed.length === 0 ? PRINT_SIZE_NO_SIZE_KEY : trimmed.toUpperCase();
    if (code !== PRINT_SIZE_NO_SIZE_KEY && !isCodeValid(code)) {
      toast.error('Size codes must be 1 to 10 letters or digits.');
      return false;
    }
    const existing = localMap[pt];
    if (existing && Object.prototype.hasOwnProperty.call(existing, code)) {
      toast.error(
        `${code === PRINT_SIZE_NO_SIZE_KEY ? 'No size (default)' : code} is already mapped for ${pt}.`,
      );
      return false;
    }
    setRule(pt, code, { widthCm: 0, heightCm: 0 });
    return true;
  };

  const handleSelectProductType = (rawCode: string): boolean => {
    const code = rawCode.trim().toUpperCase();
    if (!isCodeValid(code)) {
      toast.error('Product type codes must be 1 to 10 letters or digits.');
      return false;
    }
    if (productTypeCodes.includes(code)) {
      toast.error(`${code} is already in the list.`);
      return false;
    }
    setLocalMap((prev) => ({ ...prev, [code]: {} }));
    setOpenItems((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setHasChanges(true);
    return true;
  };

  const handleSelectSizeCode = (pt: string, code: string): boolean =>
    addSize(pt, code === PRINT_SIZE_NO_SIZE_KEY ? '' : code);

  const handleSave = (): void => {
    updateMutation.mutate({ organizationId: targetOrgInput, map: cleanMapForSave(localMap) });
  };

  // ---- client code-table pick-lists (Combobox) ----

  const { typeOptions } = useMemo(() => buildProductTypeCodeOptions(
    codeTablesQuery.data?.productTypes,
    productTypeCodes,
    {
      custom: t('production.printSizeRules.badgeCustom'),
      singleSize: t('production.printSizeRules.badgeSingleSize'),
      alreadyAdded: t('production.printSizeRules.alreadyAdded'),
      invalidCode: t('production.codeOptions.invalidCode'),
    },
  ), [codeTablesQuery.data, productTypeCodes, t]);
  const typeComboboxStatus = codeTablesStatus(codeTablesQuery, typeOptions.length);

  // Base size-code list (org-wide vocabulary, not per product type) —
  // per-type `disabled` is layered on inside PrintSizeProductTypeItem from
  // that type's OWN already-mapped sizes.
  const sizeOptionsBase: PrintSizeCodeOption[] = useMemo(() => {
    return (codeTablesQuery.data?.sizes ?? [])
      .map((row) => ({ code: row.code, label: joinStoreNames(row.storeNames) }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [codeTablesQuery.data]);

  const sizeStoreNamesByCode = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of codeTablesQuery.data?.sizes ?? []) map.set(row.code, row.storeNames);
    return map;
  }, [codeTablesQuery.data]);

  const sizeComboboxStatus = codeTablesStatus(codeTablesQuery, sizeOptionsBase.length);

  const showSizeDefaultsHint = codeTablesQuery.data != null && !codeTablesQuery.data.exists;

  // ---- states ----
  // A failed read never removes "Applies to" (an operator stuck on a client
  // whose read fails must be able to switch back), and nothing renders "no
  // rules" until the org is known and its rules have answered.
  const rulesPending = !selectedOrgId || rulesQuery.isPending;

  return (
    <div className="space-y-4">
      {/* 1. Framing. */}
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="leading-relaxed">
          Set the exact print size (in cm) for a product type and size. A saved rule overrides the
          incoming artwork&apos;s own dimensions at print time.
        </span>
      </p>

      {/* 2. Client selector. */}
      <div className="grid max-w-xs gap-1.5">
        <label htmlFor="print-size-rules-client" className="text-sm font-medium">
          Applies to
        </label>
        <Select
          value={selectedOrgId ?? ownOrgId}
          onValueChange={setSelectedOrgId}
          disabled={!ownOrgId}
        >
          <SelectTrigger id="print-size-rules-client" className="h-11">
            <SelectValue placeholder="Select an organization" />
          </SelectTrigger>
          <SelectContent>
            {ownOrgId && <SelectItem value={ownOrgId}>This organization</SelectItem>}
            {clientOptions.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 3. Body: error, initial load skeleton, empty or the rules. */}
      {rulesQuery.isError ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>Could not load print size rules. {rulesQuery.error?.message}</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void rulesQuery.refetch()}
            disabled={rulesQuery.isFetching}
            className="gap-1.5"
          >
            {rulesQuery.isFetching && (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            Retry
          </Button>
        </div>
      ) : rulesPending ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      ) : productTypeCodes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <div className="rounded-full bg-muted p-3">
            <Ruler className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">No print size rules yet</p>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              Add a product type below, then set width and height (cm) for each size.
            </p>
          </div>
        </div>
      ) : (
        <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="space-y-2">
          {productTypeCodes.map((pt) => (
            <PrintSizeProductTypeItem
              key={pt}
              productType={pt}
              sizes={localMap[pt] ?? {}}
              admin={admin}
              onSetRule={setRule}
              onRemoveSize={removeSize}
              onSelectSize={handleSelectSizeCode}
              onRemoveProductType={removeProductType}
              sizeOptions={sizeOptionsBase}
              sizeStatus={sizeComboboxStatus}
              sizeStoreNamesByCode={sizeStoreNamesByCode}
              showSizeDefaultsHint={showSizeDefaultsHint}
              t={t}
            />
          ))}
        </Accordion>
      )}

      {/* 4. Add product type (admin only) — the client's own product-sync
          types as a pick-list, degrading to free text while the query
          loads/errors (PrintSizeCodeCombobox owns both). Not while the rules
          themselves are unread: there is nothing to add them to yet. */}
      {admin && !rulesQuery.isError && !rulesPending && (
        <PrintSizeCodeCombobox
          emptyLabel={t('production.codeOptions.noMatches')}
          options={typeOptions}
          status={typeComboboxStatus}
          onSelect={handleSelectProductType}
          placeholder={t('production.printSizeRules.typeComboboxPlaceholder')}
          ariaLabel={t('production.printSizeRules.typeComboboxAriaLabel')}
          addButtonLabel={t('production.printSizeRules.addProductType')}
          noSizeLabel={t('production.printSizeRules.noSizeOption')}
          createLabel={(code) => t('production.printSizeRules.createCode', { code })}
          errorHint={t('production.printSizeRules.loadErrorHint')}
          className="sm:w-56"
        />
      )}

      {/* 5. Hint. */}
      <p className="text-xs text-muted-foreground">
        Aspect ratio is preserved: artwork is fitted inside W×H, and smaller artwork is scaled up.
        Rules override the artwork&apos;s incoming size, including source-mode clients.
      </p>
      {/* S4 review fix: rules take effect at different speeds depending on
          the drop path, and an owner's OWN-org rules only reach one of them. */}
      <p className="text-xs text-muted-foreground">{t('production.printSizeRules.applyTimingHint')}</p>

      {/* 6. Save (admin + dirty only). */}
      {admin && hasChanges && (
        <div className="flex items-center justify-end gap-3">
          {anyInvalid && (
            <span className="text-xs text-amber-700" role="status">
              Finish or fix the highlighted sizes to save.
            </span>
          )}
          <Button
            onClick={handleSave}
            // S3 review fix: also disabled while rulesQuery is (re)fetching —
            // belt-and-braces alongside the immediate dirty-buffer reset above,
            // so Save can never fire against a buffer that isn't confirmed to
            // belong to the currently selected org.
            disabled={updateMutation.isPending || anyInvalid || rulesQuery.isFetching}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            {t('production.printSizeRules.save')}
          </Button>
        </div>
      )}

      {!admin && <p className="text-sm text-muted-foreground/70">Only admins can edit print size rules.</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One product-type accordion item: size code → width/height (cm) rows.
// ---------------------------------------------------------------------------

interface PrintSizeProductTypeItemProps {
  productType: string;
  sizes: Record<string, PrintSizeRule>;
  admin: boolean;
  onSetRule: (pt: string, code: string, patch: Partial<PrintSizeRule>) => void;
  onRemoveSize: (pt: string, code: string) => void;
  onSelectSize: (pt: string, code: string) => boolean;
  onRemoveProductType: (pt: string) => void;
  /** Org-wide size-code vocabulary from the client's product-sync table. */
  sizeOptions: PrintSizeCodeOption[];
  sizeStatus: PrintSizeCodeComboboxStatus;
  sizeStoreNamesByCode: Map<string, string[]>;
  showSizeDefaultsHint: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function PrintSizeProductTypeItem({
  productType: pt,
  sizes,
  admin,
  onSetRule,
  onRemoveSize,
  onSelectSize,
  onRemoveProductType,
  sizeOptions,
  sizeStatus,
  sizeStoreNamesByCode,
  showSizeDefaultsHint,
  t,
}: PrintSizeProductTypeItemProps): JSX.Element {
  const entries = Object.entries(sizes).sort(sortSizeEntries);

  // Layer this TYPE's own already-mapped codes onto the shared vocabulary.
  const sizeOptionsForType: PrintSizeCodeOption[] = useMemo(
    () =>
      sizeOptions.map((opt) => {
        const alreadyMapped = Object.prototype.hasOwnProperty.call(sizes, opt.code);
        return alreadyMapped
          ? { ...opt, disabled: true, disabledReason: t('production.printSizeRules.alreadyAdded') }
          : opt;
      }),
    [sizeOptions, sizes, t],
  );

  const unmappedSizeCodes = useMemo(
    () => sizeOptions.filter((opt) => !Object.prototype.hasOwnProperty.call(sizes, opt.code)),
    [sizeOptions, sizes],
  );

  const handleAddAllClientSizes = (): void => {
    for (const opt of unmappedSizeCodes) onSetRule(pt, opt.code, { widthCm: 0, heightCm: 0 });
  };

  return (
    <AccordionItem value={pt} className="rounded-lg border">
      <AccordionTrigger className="rounded-t-lg px-3 py-3 hover:no-underline hover:bg-muted/30">
        <div className="flex min-w-0 items-center gap-3">
          <Ruler className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="font-mono text-sm font-semibold">{pt}</span>
          <span className="text-xs text-muted-foreground">
            {entries.length === 1 ? '1 size' : `${entries.length} sizes`}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-3 px-3 pb-4 pt-0">
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="w-28 px-3 py-2 font-medium">Size</th>
                <th className="w-28 px-3 py-2 font-medium">Width (cm)</th>
                <th className="w-28 px-3 py-2 font-medium">Height (cm)</th>
                <th className="px-3 py-2 font-medium">Preview</th>
                {admin && <th className="w-12" />}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={admin ? 5 : 4} className="px-3 py-3 text-center text-xs text-muted-foreground/70">
                    No sizes mapped yet.
                  </td>
                </tr>
              ) : (
                entries.map(([code, rule]) => {
                  const label = code === PRINT_SIZE_NO_SIZE_KEY ? t('production.printSizeRules.noSizeOption') : code;
                  const mappedStoreNames = sizeStoreNamesByCode.get(code);
                  const widthInvalid = rule.widthCm > 0 && !isCmValid(rule.widthCm);
                  const heightInvalid = rule.heightCm > 0 && !isCmValid(rule.heightCm);
                  const unset = rule.widthCm <= 0 || rule.heightCm <= 0;
                  const previewOk = isCmValid(rule.widthCm) && isCmValid(rule.heightCm);
                  return (
                    <tr key={code} className="border-b align-top last:border-b-0">
                      <td className="px-3 py-2">
                        <span className="font-mono">{label}</span>
                        {mappedStoreNames && mappedStoreNames.length > 0 && (
                          <div className="mt-0.5 truncate text-xs text-muted-foreground">
                            {mappedStoreNames.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {admin ? (
                          <Input
                            type="number"
                            inputMode="decimal"
                            step={0.5}
                            min={PRINT_SIZE_CM_MIN}
                            max={PRINT_SIZE_CM_MAX}
                            value={cmToInputValue(rule.widthCm)}
                            aria-invalid={widthInvalid || undefined}
                            aria-label={`Width in cm for ${label}`}
                            className="h-11 w-24"
                            onChange={(e) =>
                              onSetRule(pt, code, { widthCm: inputValueToCm(e.target.value) })
                            }
                          />
                        ) : (
                          <span className="font-mono">{rule.widthCm > 0 ? rule.widthCm : '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {admin ? (
                          <Input
                            type="number"
                            inputMode="decimal"
                            step={0.5}
                            min={PRINT_SIZE_CM_MIN}
                            max={PRINT_SIZE_CM_MAX}
                            value={cmToInputValue(rule.heightCm)}
                            aria-invalid={heightInvalid || undefined}
                            aria-label={`Height in cm for ${label}`}
                            className="h-11 w-24"
                            onChange={(e) =>
                              onSetRule(pt, code, { heightCm: inputValueToCm(e.target.value) })
                            }
                          />
                        ) : (
                          <span className="font-mono">{rule.heightCm > 0 ? rule.heightCm : '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {previewOk ? (
                          <span className="font-mono">
                            {cmToPx(rule.widthCm)} × {cmToPx(rule.heightCm)} px
                          </span>
                        ) : unset ? (
                          'Enter width and height.'
                        ) : (
                          <span className="text-destructive">Out of range (1 to 120cm).</span>
                        )}
                      </td>
                      {admin && (
                        <td className="px-2 py-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 text-muted-foreground hover:text-foreground"
                            aria-label={`Remove ${label} from ${pt}`}
                            onClick={() => onRemoveSize(pt, code)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {admin && (
          <div className="space-y-1.5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <PrintSizeCodeCombobox
                emptyLabel={t('production.codeOptions.noMatches')}
                options={sizeOptionsForType}
                allowNoSize
                status={sizeStatus}
                onSelect={(code) => onSelectSize(pt, code)}
                placeholder={t('production.printSizeRules.sizeComboboxPlaceholder')}
                ariaLabel={t('production.printSizeRules.sizeComboboxAriaLabel', { productType: pt })}
                addButtonLabel={t('production.printSizeRules.addSize')}
                noSizeLabel={t('production.printSizeRules.noSizeOption')}
                createLabel={(code) => t('production.printSizeRules.createCode', { code })}
                errorHint={t('production.printSizeRules.loadErrorHint')}
              />
              {sizeStatus === 'loaded' && unmappedSizeCodes.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 gap-1"
                  onClick={handleAddAllClientSizes}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t('production.printSizeRules.addAllClientSizes')}
                </Button>
              )}
              <Button
                variant="ghost"
                className="h-11 gap-1 text-muted-foreground hover:text-destructive"
                onClick={() => onRemoveProductType(pt)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Remove {pt}
              </Button>
            </div>
            {showSizeDefaultsHint && (
              <p className="text-xs text-muted-foreground">{t('production.printSizeRules.sizeDefaultsHint')}</p>
            )}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
